/**
 * PMS Integration Query Hooks
 *
 * Caches the firm's PMS connection status globally so consumers that
 * remount across navigation (e.g. the main-nav "Import Matter" button,
 * which lives in two structurally different sidebar trees swapped by
 * route) read from cache instead of starting from a blank state — see
 * `ImportMatterTrigger` (LF-4762).
 */

'use client';

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey
} from '@tanstack/react-query';
import { isSuccessResult } from '@/lib/types/actions';
import { getPmsStatus } from './actions';
import {
  getPmsContactTypes,
  refreshPmsContactTypes
} from './contact-types-client';
import { publishesContactTypeCatalogue } from './contact-type-catalogue-state';
import { getPmsMatterContacts } from './import-client';
import {
  getPmsMatterContactBindings,
  unbindPmsMatterContact
} from './contact-binding-client';
import { pmsContactBindingsPollInterval } from './contact-binding-state';
import {
  runHasOutstandingWork,
  type AuditRunWatch
} from './document-sync-audit-state';
import {
  getDocumentSyncAudit,
  triggerDocumentSyncAudit
} from './document-sync-audit-client';
import type {
  DocumentSyncAuditItem,
  PmsContactTypeOption,
  PmsProvider
} from './types';

/** React Query key for the firm's PMS integration status (firm is implicit via auth). */
export const PMS_STATUS_QUERY_KEY: QueryKey = ['pms-integration', 'status'];

export function usePmsStatus() {
  return useQuery({
    queryKey: PMS_STATUS_QUERY_KEY,
    queryFn: async () => {
      const result = await getPmsStatus();
      if (!isSuccessResult(result)) {
        throw new Error(result.error || 'Failed to fetch PMS status');
      }
      return result.data;
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}

/** React Query key for the firm's PMS contact-type catalogue (firm is implicit via auth). */
export const pmsContactTypesQueryKey = (provider: PmsProvider): QueryKey => [
  'pms-integration',
  'contact-types',
  provider
];

/**
 * The firm's PMS contact-type catalogue.
 *
 * Cached for the session: the catalogue is slow-changing firm configuration, and the contact card
 * would otherwise refetch it on every open. Rendering an *existing* selection needs none of this —
 * the chosen type's label is stored with the selection — so this is only ever loaded to populate the
 * picker.
 */
export function usePmsContactTypes(
  provider: PmsProvider | null | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: pmsContactTypesQueryKey(provider ?? 'smokeball'),
    queryFn: () => getPmsContactTypes(provider!),
    enabled: enabled && !!provider,
    staleTime: 30 * 60 * 1000
  });
}

/**
 * Whether to offer a PMS contact-type selector, and what to offer in it.
 *
 * **The gate is firm-level and lives here, on the frontend.** The backend is deliberately
 * provider-neutral and applies no feature-flag check of its own, so deciding *who sees the selector*
 * is the UI's job. A matter-scoped gate is the thing to avoid: it would mean re-deriving the
 * backend's `isMatterDestinedForPms` predicate here — a second, drifting definition — and it could
 * not work at matter creation at all, where no persisted matter exists to ask about.
 *
 * The test is *"does the firm's active PMS supply a catalogue?"*, never a provider-name check.
 * Smokeball implements no contact-role hook and so returns an empty list, which reads as "offer no
 * selector" — it degrades by interface, exactly as the backend intends.
 */
export function usePmsContactTypeSelection(): {
  provider: PmsProvider | null;
  contactTypes: PmsContactTypeOption[];
  isAvailable: boolean;
  /**
   * Whether the firm's PMS publishes a catalogue at all — `true` while it has never been asked.
   *
   * Wider than `isAvailable` by exactly one case, and the two must not be conflated: a never-synced
   * catalogue has nothing to pick from, but is precisely what the manual refresh exists to populate.
   *
   * **This is the selector's own visibility gate since VALE-587**, not just the refresh's. The
   * refresh moved from the integration card onto the picker, so the picker has to render in the
   * never-synced case to carry it — a section showing an empty, disabled control plus the refresh
   * that fills it. `isAvailable` remains the narrower "is there anything to choose?" test, and is
   * what still decides whether a *select row* is offered. See {@link publishesContactTypeCatalogue}.
   */
  publishesCatalogue: boolean;
  isLoading: boolean;
} {
  const { data: status, isLoading: isStatusLoading } = usePmsStatus();

  const provider = status?.isEnabled ? (status.provider ?? null) : null;

  const { data: catalogue, isLoading: isCatalogueLoading } =
    usePmsContactTypes(provider);

  const options = catalogue?.contactTypes ?? [];

  return {
    provider,
    contactTypes: options,
    isAvailable: !!provider && options.length > 0,
    publishesCatalogue: !!provider && publishesContactTypeCatalogue(catalogue),
    isLoading: isStatusLoading || (!!provider && isCatalogueLoading)
  };
}

/**
 * Re-read the firm's contact-type catalogue from the provider on demand.
 *
 * **Seeds the cache with the response rather than only invalidating it.** The refresh already
 * returns exactly what the query would refetch, so seeding makes the picker reflect the change
 * without a second round trip — and without the window in which an invalidated query is briefly
 * loading while the user is being told the refresh succeeded.
 *
 * Nothing is written on failure (the backend leaves the previous catalogue in place), so an error
 * deliberately touches the cache not at all: the stale-but-real list the user is looking at is still
 * what is stored.
 */
export function useRefreshPmsContactTypes(provider: PmsProvider | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => refreshPmsContactTypes(provider!),
    onSuccess: (catalogue) => {
      queryClient.setQueryData(pmsContactTypesQueryKey(provider!), catalogue);
    }
  });
}

/** React Query key for one PMS matter's contacts. */
export const pmsMatterContactsQueryKey = (
  provider: PmsProvider,
  externalMatterId: string
): QueryKey => [
  'pms-integration',
  'matter-contacts',
  provider,
  externalMatterId
];

/**
 * The named people and companies on a PMS matter, for identifying its decedent before import.
 *
 * Naming them costs the backend one PMS call per contact, so this is `enabled`-gated: the import
 * modal only asks once the user has chosen a matter type that actually needs a decedent, and the
 * answer is cached for the life of the dialog so re-opening the picker or switching type back and
 * forth does not re-fetch.
 */
export function usePmsMatterContacts(
  provider: PmsProvider | undefined,
  externalMatterId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: pmsMatterContactsQueryKey(
      provider ?? 'smokeball',
      externalMatterId ?? ''
    ),
    queryFn: () => getPmsMatterContacts(provider!, externalMatterId!),
    enabled: enabled && !!provider && !!externalMatterId,
    staleTime: 5 * 60 * 1000
  });
}

/** React Query key for one matter's PMS contact-type bindings. */
export const pmsMatterContactBindingsQueryKey = (
  provider: PmsProvider | undefined,
  matterId: number | undefined
): QueryKey => [
  'pms-integration',
  'matter-contact-bindings',
  provider ?? null,
  matterId ?? null
];

/**
 * The matter's PMS contact-type selections and their bind outcomes.
 *
 * Matter-scoped rather than per-contact so a card list costs one request rather than one per row.
 * `enabled`-gated on the firm actually having a provider — a firm with no PMS has no bindings to
 * read, and asking anyway would 404 on every matter page.
 *
 * **Polls itself while any binding is in flight** (VALE-159), so a contact whose bind is still
 * landing resolves in place instead of waiting for a manual refresh — the rule, and why it is
 * bounded, is {@link pmsContactBindingsPollInterval}. The poll is deliberately part of the query
 * rather than opt-in per caller: every surface reading a binding wants the same convergence, and an
 * option only creates a way for one of them to be stale. It does not run while the tab is
 * backgrounded — nobody is watching a badge they cannot see.
 */
export function usePmsMatterContactBindings(
  provider: PmsProvider | undefined,
  matterId: number | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: pmsMatterContactBindingsQueryKey(provider, matterId),
    queryFn: () => getPmsMatterContactBindings(provider!, matterId!),
    enabled: enabled && !!provider && !!matterId,
    staleTime: 60 * 1000,
    refetchInterval: (query) =>
      pmsContactBindingsPollInterval(
        query.state.data,
        query.state.dataUpdateCount
      ),
    refetchIntervalInBackground: false
  });
}

/**
 * Unbind a contact from the matter in the PMS.
 *
 * **Only ever call this from an explicit user action.** Vale never unbinds on the user's behalf: it
 * does not reconcile a removal made in the PMS, and changing a contact's type deliberately leaves the
 * old link in place. This hook is that manual cleanup, and wiring it into any automatic flow would
 * defeat the design rather than improve it.
 *
 * Invalidates only the bindings query: a failed unbind leaves the stored row untouched server-side,
 * so a refetch after an error correctly shows the contact still bound.
 */
export function useUnbindPmsMatterContact(
  provider: PmsProvider | undefined,
  matterId: number | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactId: number) =>
      unbindPmsMatterContact(provider!, matterId!, contactId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: pmsMatterContactBindingsQueryKey(provider, matterId)
      });
    }
  });
}

/** React Query key for the firm's document sync audit (firm is implicit via auth). */
export const documentSyncAuditQueryKey = (provider: PmsProvider): QueryKey => [
  'pms-integration',
  'document-sync-audit',
  provider
];

/**
 * How often to re-read the audit while a run is in flight.
 *
 * There is no progress store by design, so this poll **is** the progress indicator: a row leaving the
 * list is what says it filed. 5s keeps a small backlog feeling live without hammering an endpoint
 * that runs two set-based queries per call; a run only polls while something is still outstanding
 * (`isRunSettled`), so the cost is bounded by the run rather than by the dialog being open.
 */
export const DOCUMENT_SYNC_AUDIT_POLL_INTERVAL_MS = 5_000;

/**
 * The outbound documents the firm's PMS is missing.
 *
 * `enabled`-gated on the dialog actually being open: it is a two-query read the integrations page has
 * no use for until someone asks for it. **Deliberately not gated on the connection being healthy** —
 * a lapsed connection and a firm with automatic filing switched off are the two states this report
 * exists for, and both answer correctly because the audit reads Vale's own records rather than the PMS.
 *
 * `staleTime: 0` because the whole point is freshness: a row that has since been filed must not be
 * offered for filing again, and the trigger reports such a row back as `not-owed` rather than acting.
 */
export function useDocumentSyncAudit(
  provider: PmsProvider | null | undefined,
  {
    enabled = true,
    watch = null
  }: { enabled?: boolean; watch?: AuditRunWatch | null } = {}
) {
  return useQuery({
    queryKey: documentSyncAuditQueryKey(provider ?? 'smokeball'),
    queryFn: () => getDocumentSyncAudit(provider!),
    enabled: enabled && !!provider,
    staleTime: 0,
    /*
      The stop condition is applied to the query's OWN data rather than to a boolean the caller
      derives from it. That ordering is forced: a caller computing "is the run still moving?" needs
      the rows, the rows need this fetch, and this fetch would need the answer — so the predicate has
      to live where the data already is. `runHasOutstandingWork` is pure, so it stays testable.
    */
    refetchInterval: (query) =>
      runHasOutstandingWork(watch, query.state.data?.items)
        ? DOCUMENT_SYNC_AUDIT_POLL_INTERVAL_MS
        : false,
    // Nobody is watching a dialog on a backgrounded tab.
    refetchIntervalInBackground: false
  });
}

/**
 * File the selected backlog.
 *
 * **Does not invalidate on success, deliberately.** The response reports what was *started*, and the
 * uploads outlive it — so refetching immediately would re-read a list nothing has changed yet and
 * make the run look like a no-op. The caller starts the poll above instead, which is what observes
 * rows dropping off as receipts land.
 *
 * The `409` is a resolved value rather than a thrown error (see `triggerDocumentSyncAudit`), so
 * `onSuccess` sees both outcomes and the caller discriminates on `outcome`.
 */
export function useTriggerDocumentSyncAudit(provider: PmsProvider | null) {
  return useMutation({
    mutationFn: (
      items: Array<Pick<DocumentSyncAuditItem, 'itemId' | 'kind'>>
    ) => triggerDocumentSyncAudit(provider!, items)
  });
}
